import React, { useEffect, useState } from "react";
import { DataTable } from "@/components/ui/data-table";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaginationControls } from "@/components/common/PaginationControl";
import {
  PerkPointReportsApi,
  PointsExpiryReportItem,
} from "@/api/giftcard.api";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";
import { getFormattedDateTime } from "@/utils";

export function ReportTransaction() {
  const [reportRows, setReportRows] = useState<PointsExpiryReportItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const { toast } = useToast();

  const formatDate = (dateString: string | null): string => {
    if (!dateString || dateString === "0001-01-01T00:00:00") {
      return "-";
    }
    return getFormattedDateTime(dateString) || "-";
  };

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const response = await PerkPointReportsApi.getPointsExpiryReport({
        p_PageNumber: currentPage + 1,
        p_PageSize: pageSize,
      });

      setReportRows(
        Array.isArray(response.expiryResponse) ? response.expiryResponse : []
      );
      setTotalRecords(response.total_records ?? 0);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Warning",
          description: response.error_msg,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching points expiry report:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch points expiry report",
        variant: "destructive",
      });
      setReportRows([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-800">
          Points Expiry Report
        </h2>
        <p className="text-sm text-muted-foreground">
          Points expiry report for loyalty members.
        </p>
      </div>

      {isLoading ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <div className="space-y-4">
          <DataTable
            title=""
            data={reportRows}
            cellClassName="py-6 text-gray-700"
            columns={[
              {
                header: "Mobile No",
                accessorKey: "mobileNo" as keyof PointsExpiryReportItem,
              },
              {
                header: "Name",
                accessorKey: "name" as keyof PointsExpiryReportItem,
                cell: (row: PointsExpiryReportItem) => row.name || "-",
              },
              {
                header: "Outlet Name",
                accessorKey: "outletName" as keyof PointsExpiryReportItem,
                cell: (row: PointsExpiryReportItem) => row.outletName || "-",
              },
              {
                header: "Expired Points",
                accessorKey: "expiredPoints" as keyof PointsExpiryReportItem,
                cell: (row: PointsExpiryReportItem) =>
                  row.expiredPoints != null
                    ? row.expiredPoints.toString()
                    : "-",
              },
              {
                header: "Expired Date",
                accessorKey: "expiredDate" as keyof PointsExpiryReportItem,
                cell: (row: PointsExpiryReportItem) =>
                  formatDate(row.expiredDate),
              },
            ]}
          />

          {totalRecords > 0 && (
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Rows per page:</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(parseInt(value, 10));
                    setCurrentPage(0);
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="30">30</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPrevHandler={() =>
                  setCurrentPage((page) => Math.max(0, page - 1))
                }
                onNextHandler={() =>
                  setCurrentPage((page) =>
                    Math.min(totalPages - 1, page + 1)
                  )
                }
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
